import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import Button from "@shared/v2/Button";
import Pagination from "@shared/v2/Pagination";
import TextComboInput from "@shared/v2/TextComboInput";
import TextLink from "@shared/v2/TextLink";
import TextToggle from "@shared/v2/TextToggle";
import Dropdown from "@shared/v2/Dropdown";
import FieldLabel from "@shared/v2/FieldLabel";
import { MagnifyingGlassSolidV6, PlusSolidV6, SpinnerSolidV6 } from "@shared/v2/Icomoon";
import { useDebounce } from "@shared/hookHelpers";
import { useDispatch } from "react-redux";
import { useWebsiteIDXLoading, useWebsiteIDXUrl } from "../../reducers/websiteReducer/selectors";
import { useCurrentUser, useFeatureFlags } from "../../reducers/layoutReducer/selectors";
import { Table, TBody, TD, TH, THead, TR } from "../../Reports/components/BusinessDashboard/Table/Table";
import { isTokenValid } from "./api";
import AreaPageRow from "./AreaPageRow";
import Loading from "../../Goals/components/Loader";
import NoPermission from "../shared/NoPermission";
import NoWebsites from "../shared/NoWebsites";
import WebsiteHeader from "../shared/WebsiteHeader";
import ShareItemModal from "../shared/ShareItemModal";

import {
  getToken,
  getSites,
  getPages,
  syncSitesWithPageMaker,
  getSiteMlsIds,
} from "../../reducers/websiteReducer/areaPageReducer";
import {
  usePages,
  useSites,
  useSiteUuid,
  useToken,
  useTokenExpiresAt,
} from "../../reducers/websiteReducer/areaPageSelectors";

const STATUS_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Published", value: "published" },
  { label: "Drafts", value: "drafts" },
  { label: "Archived", value: "archived" },
];

// Area Pages index — list + empty state. Data comes from a mock until the backend exists.
const AreaPagesIndex = () => {
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const featureFlags = useFeatureFlags();
  const idxLoading = useWebsiteIDXLoading();
  const idxUrl = useWebsiteIDXUrl();
  const navigate = useNavigate();
  const token = useToken();
  const tokenExpiresAt = useTokenExpiresAt();
  // const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [publishStatus, setPublishStatus] = useState("all");
  const [orderBy, setOrderBy] = useState("dateCreated");
  const [sortOrder, setSortOrder] = useState("desc");
  const [search, setSearch] = useState("");
  const searchTerm = useDebounce(search, 250);
  const [sharePage, setSharePage] = useState(null);
  const [shareSite, setShareSite] = useState(null);
  const sites = useSites();
  const siteUuid = useSiteUuid();
  const site = sites?.find((s) => s.uuid === siteUuid);
  const siteDomain = (s) => `${s.subdomain ? `${s.subdomain}.` : ""}${s.domain}`;
  const shareSiteOptions = (sites || []).map((s) => ({ label: siteDomain(s), value: s.uuid }));
  const selectedShareSite = shareSite || site;
  const shareUrl =
    selectedShareSite && sharePage
      ? `https://${siteDomain(selectedShareSite)}/area${sharePage.path || `/${sharePage.slug}`}`
      : "";

  const pages = usePages();
  const [filteredPages, setFilteredPages] = useState(pages);

  const counts = useMemo(() => {
    const byStatus = { published: 0, drafts: 0, archived: 0 };
    (pages || []).forEach((p) => {
      if (p.isActive === false) byStatus.archived += 1;
      else if (p.settings.status === "published") byStatus.published += 1;
      else if (p.settings.status === "draft") byStatus.drafts += 1;
    });
    return { all: (pages || []).length, ...byStatus };
  }, [pages]);

  // Per the Figma, every filter tab carries its count except Archived.
  const statusOptions = useMemo(
    () =>
      STATUS_OPTIONS.map((option) => ({
        ...option,
        label: option.value === "archived" ? option.label : `${option.label} (${counts[option.value]})`,
      })),
    [counts],
  );

  // Entering Area Pages syncs the account's sites into Page Maker.
  useEffect(() => {
    const load = async () => {
      try {
        await dispatch(syncSitesWithPageMaker()).unwrap();
      } catch {
        // Sync is best-effort here; a valid token can still list existing pages.
      }
      try {
        if (!isTokenValid(token, tokenExpiresAt)) {
          await dispatch(getToken()).unwrap();
        }
      } catch {
        // Without a token the pages can never load; land on the empty state.
        setFilteredPages([]);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!token) return;

    const load = async () => {
      setLoading(true);
      try {
        await dispatch(getSites()).unwrap();
        await dispatch(getPages()).unwrap();
      } catch {
        // No sites synced yet (or the fetch failed): nothing to list.
        setFilteredPages([]);
      } finally {
        setLoading(false);
      }
      // MLS ids only scope the featured-listings search; their lookup failing must not
      // blank an already-loaded list (rejected leaves mlsIds unset and consumers fall back).
      dispatch(getSiteMlsIds());
    };
    load();
  }, [token]);

  useEffect(() => {
    if (!pages) return;
    const statusOf = (p) => {
      if (p.isActive === false) return "archived";
      if (p.settings.status === "published") return "published";
      if (p.settings.status === "draft") return "drafts";
      return undefined;
    };

    const matchesStatus = (p, status) => status === "all" || statusOf(p) === status.toLowerCase();

    const filtered = pages
      .filter((p) => matchesStatus(p, publishStatus))
      .filter((p) => !searchTerm || p.name.toLowerCase().includes(searchTerm.toLowerCase()));

    const sorted = [...filtered].sort((a, b) => {
      const dir = sortOrder === "asc" ? 1 : -1;
      const av = orderBy === "status" ? statusOf(a) : a[orderBy] || "";
      const bv = orderBy === "status" ? statusOf(b) : b[orderBy] || "";
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });

    setFilteredPages(sorted);
  }, [pages, publishStatus, orderBy, sortOrder, searchTerm]);

  // useEffect(() => {
  //   if (!idxUrl) return;
  //   getAreaPageStatusCounts().then(setCounts);
  // }, [idxUrl]);

  // useEffect(() => {
  //   if (!idxUrl) return;
  //   setLoading(true);
  //   getAreaPages({ page, itemsPerPage: ITEMS_PER_PAGE, publishStatus, orderBy, sortOrder, searchTerm })
  //     .then((res) => {
  //       setPages(res.pages);
  //       setTotalPages(res.totalPages);
  //     })
  //     .finally(() => setLoading(false));
  // }, [idxUrl, page, publishStatus, orderBy, sortOrder, searchTerm]);

  const onSort = (field) => () => {
    if (field === orderBy) {
      setSortOrder(sortOrder === "desc" ? "asc" : "desc");
    } else {
      setOrderBy(field);
      setSortOrder("desc");
    }
  };

  if (!featureFlags.areaPages) return <Navigate to="/blog" replace />;

  if (!currentUser.account.isAdmin) {
    return (
      <div className="tw-flex-1 tw-h-full tw-max-w-[1416px] tw-mx-auto tw-flex tw-gap-[32px]">
        <div className="tw-relative tw-w-full tw-flex tw-flex-col tw-gap-[32px] tw-m-[16px] tw-bg-white tw-border-[1px] tw-border-solid tw-border-gray-10 tw-rounded-[8px] tw-p-[32px] tw-overflow-auto">
          <NoPermission
            title="You do not have permission to create website pages"
            body="Only admins on your team can use this feature. Reach out to an admin if you need to have a new page built out."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="tw-flex-1 tw-h-full tw-max-w-[1416px] tw-mx-auto tw-flex tw-gap-[32px]">
      <div className="tw-relative tw-w-full tw-flex tw-flex-col tw-gap-[32px] tw-m-[16px] tw-bg-white tw-border-[1px] tw-border-solid tw-border-gray-10 tw-rounded-[8px] tw-p-[32px] tw-overflow-auto">
        {idxLoading && <Loading />}
        {!idxLoading && !idxUrl && <NoWebsites target="area pages" />}
        {idxUrl && (
          <>
            <WebsiteHeader
              title="Area Pages"
              description={
                <>
                  Boost SEO, increase user engagement, and get more leads by creating pages that show off the
                  areas you serve. Pages you create here will automatically be added to your site’s navigation
                  menu unless you decide to exclude them.{" "}
                  <TextLink
                    href="https://www.brivityknowledge.com/docs/blog-management-in-brivity"
                    target="_blank"
                  >
                    Learn more.
                  </TextLink>
                </>
              }
              actions={
                <Button
                  size="medium"
                  className="tw-whitespace-nowrap"
                  onClick={() => navigate("/area_pages/new")}
                >
                  <PlusSolidV6 /> New Page
                </Button>
              }
            />
            <div className="tw-flex tw-flex-col tw-gap-[16px]">
              <div className="tw-flex tw-flex-wrap tw-justify-between tw-items-center tw-gap-[16px]">
                <div className="tw-flex tw-items-center tw-gap-[16px]">
                  <TextToggle
                    options={statusOptions}
                    value={publishStatus}
                    onChange={(e) => {
                      setPage(1);
                      setPublishStatus(e.target.value);
                    }}
                  />
                  {loading && <SpinnerSolidV6 size="xl" className="tw-animate-spin" />}
                </div>
                <TextComboInput
                  className="!tw-z-auto"
                  containerClassName="!tw-max-w-[400px] tw-w-full"
                  placeholder="Search by page title"
                  trailing={<MagnifyingGlassSolidV6 size="l" className="tw-text-gray-30" />}
                  value={search}
                  onChange={(e) => {
                    setPage(1);
                    setSearch(e.target.value);
                  }}
                />
              </div>
            </div>
            <div className="tw-mt-[-16px] tw-flex-1 tw-overflow-auto tw-min-h-[200px]">
              <Table className="tw-border-separate">
                <THead className="tw-sticky tw-top-0 tw-bg-white">
                  <TR className="tw-font-semibold">
                    <TH border="tw-border-y-[1px] tw-border-x-0" />
                    <TH border="tw-border-y-[1px] tw-border-x-0">Page Title</TH>
                    <TH border="tw-border-y-[1px] tw-border-x-0">Area</TH>
                    <TH
                      border="tw-border-y-[1px] tw-border-x-0"
                      className="tw-text-center"
                      sort={orderBy === "status" && sortOrder}
                      onSort={onSort("status")}
                    >
                      Status
                    </TH>
                    <TH
                      border="tw-border-y-[1px] tw-border-x-0"
                      sort={orderBy === "datePublished" && sortOrder}
                      onSort={onSort("datePublished")}
                    >
                      Publish Date
                    </TH>
                    <TH
                      border="tw-border-y-[1px] tw-border-x-0"
                      sort={orderBy === "dateCreated" && sortOrder}
                      onSort={onSort("dateCreated")}
                    >
                      Created
                    </TH>
                    <TH className="tw-text-center" border="tw-border-y-[1px] tw-border-x-0">
                      Created By
                    </TH>
                    <TH border="tw-border-y-[1px] tw-border-x-0" />
                  </TR>
                </THead>
                <TBody>
                  {!loading && filteredPages?.length === 0 && (
                    <TR>
                      <TD
                        colSpan={8}
                        className="tw-text-center tw-text-gray-50 tw-py-[16px]"
                        border="tw-border-b-[1px] tw-border-r-0"
                      >
                        No pages yet
                      </TD>
                    </TR>
                  )}
                  {filteredPages?.map((areaPage) => (
                    <AreaPageRow
                      key={areaPage.uuid}
                      areaPage={areaPage}
                      setSharePage={setSharePage}
                      site={site}
                    />
                  ))}
                </TBody>
              </Table>
            </div>
          </>
        )}
        {totalPages > 1 && (
          <div className="tw-flex tw-justify-center">
            <Pagination currentPage={page} totalPages={totalPages} onGoToPage={(p) => setPage(p)} />
          </div>
        )}

        <ShareItemModal
          title="Share Page"
          item={sharePage}
          onClose={() => {
            setSharePage(null);
            setShareSite(null);
          }}
          url={shareUrl}
          action={
            <>
              <FieldLabel label="Which site would you like to send viewers to" />
              <Dropdown
                options={shareSiteOptions}
                value={shareSiteOptions.find((o) => o.value === selectedShareSite?.uuid) || null}
                onChange={(o) => setShareSite(sites?.find((s) => s.uuid === o.value) || null)}
              />
            </>
          }
        />
      </div>
    </div>
  );
};

export default AreaPagesIndex;
